/**
 * Redux slice for transcript management
 * Handles transcript segments, accumulative text, and real-time updates
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { sessionService } from '../services/sessionService';
import { fetchSession } from './sessionSlice';
import { LOADING_STATE } from '../types/constants';

// ===== TYPES =====

export interface TranscriptSegment {
  id: string;
  transcript_id: string;
  text: string;
  speaker: string | null;
  timestamp: number;
  isPartial: boolean;  // true = in-progress AWS Transcribe result, false = confirmed result
  startTime?: number;
  endTime?: number;
  // Bilingual/multilingual support
  detectedLanguage?: string;  // e.g., "en-US", "es-US"
  segmentId?: string;  // Backend-generated segment ID for matching translations
  translations?: Record<string, string>;  // e.g., {"es": "Hola", "en": "Hello"}
}

export interface TranscriptInfo {
  transcript_id: string;
  audio_id: string;
  created_by?: string | null;
  status: 'processing' | 'postprocessing' | 'completed' | 'postprocess_failed';
  created_at: string;
  completed_at: string | null;
  evidence?: Record<string, string[]>;  // {field_name: [quote1, quote2, ...]}
}

interface TranscriptState {
  // Transcript segments (both partial and final)
  segments: TranscriptSegment[];
  
  // Accumulative transcript (final text only)
  accumulativeText: string;
  
  // Current partial text (for display alongside accumulative)
  currentPartialText: string;
  
  // Segment tracking
  lastSegmentId: string | null;
  segmentCount: number;
  wordCount: number;
  
  transcripts: TranscriptInfo[];
  
  // Loading state
  fetchTranscriptStatus: LOADING_STATE;
  error: string | null;
}

// ===== INITIAL STATE =====

const initialState: TranscriptState = {
  segments: [],
  accumulativeText: '',
  currentPartialText: '',
  lastSegmentId: null,
  segmentCount: 0,
  wordCount: 0,
  transcripts: [],
  fetchTranscriptStatus: 'idle',
  error: null,
};

// ===== ASYNC THUNKS =====

export const fetchTranscript = createAsyncThunk(
  'transcript/fetchTranscript',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      const transcript = await sessionService.fetchTranscript(sessionId);
      return transcript;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch transcript');
    }
  }
);

// ===== SLICE =====

const transcriptSlice = createSlice({
  name: 'transcript',
  initialState,
  reducers: {
    addTranscriptSegment: (state, action: PayloadAction<TranscriptSegment>) => {
      const segment = action.payload;
      
      if (!segment.isPartial) {
        // Confirmed result: add to segments and update accumulative text
        state.segments.push(segment);
        
        // Append to accumulative text
        if (state.accumulativeText) {
          state.accumulativeText += ' ' + segment.text;
        } else {
          state.accumulativeText = segment.text;
        }
        
        // Clear current partial
        state.currentPartialText = '';
        
        // Update counters
        state.segmentCount = state.segments.length;
        state.wordCount = state.accumulativeText.split(/\s+/).filter(w => w.length > 0).length;
        state.lastSegmentId = segment.id;
      } else {
        // Partial result: update or add to segments, update current partial text
        const existingIndex = state.segments.findIndex(s => s.id === segment.id);
        
        if (existingIndex >= 0) {
          // Update existing partial segment
          state.segments[existingIndex] = segment;
        } else {
          // Add new partial segment
          state.segments.push(segment);
        }
        
        // Update current partial text for accumulative display
        state.currentPartialText = segment.text;
      }
    },
    
    updateAccumulativeText: (state, action: PayloadAction<{ text: string; isPartial: boolean }>) => {
      const { text, isPartial } = action.payload;
      
      if (!isPartial) {
        // Add to accumulative
        if (state.accumulativeText) {
          state.accumulativeText += ' ' + text;
        } else {
          state.accumulativeText = text;
        }
        state.currentPartialText = '';
        state.wordCount = state.accumulativeText.split(/\s+/).filter(w => w.length > 0).length;
      } else {
        // Update partial
        state.currentPartialText = text;
      }
    },
    
    clearTranscript: (state) => {
      state.segments = [];
      state.accumulativeText = '';
      state.currentPartialText = '';
      state.lastSegmentId = null;
      state.segmentCount = 0;
      state.wordCount = 0;
    },
    
    removePartialSegments: (state) => {
      // Remove all partial segments (keep only confirmed)
      state.segments = state.segments.filter(s => !s.isPartial);
      state.currentPartialText = '';
    },
    
    resetTranscript: (state) => {
      return {
        ...initialState,
        fetchTranscriptStatus: 'idle',
        error: null,
      };
    },

    /**
     * Update translations for an existing segment (Phase 2 of bilingual flow).
     * Matches by segmentId and merges translations map.
     */
    updateSegmentTranslation: (state, action: PayloadAction<{ segmentId: string; translations: Record<string, string> }>) => {
      const { segmentId, translations } = action.payload;
      const segment = state.segments.find(s => s.segmentId === segmentId);
      if (segment) {
        segment.translations = { ...(segment.translations || {}), ...translations };
      }
    },
  },
  extraReducers: (builder) => {
    // Handle fetchTranscript lifecycle
    builder
      .addCase(fetchTranscript.pending, (state) => {
        state.fetchTranscriptStatus = 'pending';
        state.error = null;
      })
      .addCase(fetchTranscript.fulfilled, (state, action) => {
        state.fetchTranscriptStatus = 'fulfilled';
        state.error = null;
        
        state.transcripts = action.payload.transcripts || [];
        
        const persistedSegments = action.payload.segments.map((seg: any, idx: number) => ({
          id: `persisted-${idx}`,
          transcript_id: seg.transcript_id,
          text: seg.text,
          speaker: seg.speaker,
          timestamp: new Date(seg.created_at).getTime(),
          isPartial: false,
          startTime: seg.start_time,
          endTime: seg.end_time,
          // Bilingual fields (from backend)
          detectedLanguage: seg.detected_language || undefined,
          translations: seg.translations || undefined,
        }));
        
        // Replace current segments with persisted ones
        state.segments = persistedSegments;
        state.segmentCount = persistedSegments.length;
        
        // Clear live WebSocket text since we now have persisted data
        // Without this, leftover partial/accumulative text shows as duplicate grey text
        state.accumulativeText = '';
        state.currentPartialText = '';
      })
      .addCase(fetchTranscript.rejected, (state, action) => {
        state.fetchTranscriptStatus = 'rejected';
        state.error = action.payload as string;
      });
  },
});

export const {
  addTranscriptSegment,
  updateAccumulativeText,
  clearTranscript,
  removePartialSegments,
  resetTranscript,
  updateSegmentTranslation,
} = transcriptSlice.actions;

// ===== SELECTORS =====

/**
 * Selector to get merged segments (consecutive same-speaker segments combined)
 * Only includes confirmed (non-partial) segments
 */
export const selectMergedSegments = (state: { transcript: TranscriptState }) => {
  const segments = state.transcript.segments.filter(s => !s.isPartial);
  
  if (segments.length === 0) return [];
  
  const merged: Array<{
    speaker: string | null;
    text: string;
    timestamp: number;
    transcript_id: string;
  }> = [];
  
  let currentMerged: {
    speaker: string | null;
    text: string;
    timestamp: number;
    transcript_id: string;
  } | null = null;
  
  for (const segment of segments) {
    if (!currentMerged || 
        currentMerged.speaker !== segment.speaker || 
        currentMerged.transcript_id !== segment.transcript_id) {
      if (currentMerged) {
        merged.push(currentMerged);
      }
      currentMerged = {
        speaker: segment.speaker,
        text: segment.text,
        timestamp: segment.timestamp,
        transcript_id: segment.transcript_id
      };
    } else {
      currentMerged.text += ' ' + segment.text;
    }
  }
  
  if (currentMerged) {
    merged.push(currentMerged);
  }
  
  return merged;
};

export default transcriptSlice.reducer;
